// 📦 Import des dépendances React & MUI
import React, { useState, useEffect } from "react";
import "./ContactTable.css";
import { Box, Tooltip } from "@mui/material";
import { useMediaQuery } from "@mui/material";

import axios from "axios";
import AddContactForm from "./AddContactForm.js";
import BoutonAllerAuSondage from "./BoutonAllerAuSondage.js";

import { useLanguage } from '../context/LanguageContext.js';

import { buildFullNumber, nettoyerPhone, formatInternationalPhone, isValidPhone } from "../utils/phoneUtils.js";
import ImportContactsMobile from "../components/ImportContactsMobile.js";
import { createContactRowUpdate } from "../utils/createContactRowUpdate.js";
import { logDev } from "../utils/logger.js";
import { normalizePhoneNumber } from "../utils/normalizePhoneNumber.js";

import ContactViewToggleButton from "../components/ContactViewToggleButton.js";
import ContactCardsView from "../components/ContactCardsView.js";
import EmptyStateMessage from "../components/EmptyStateMessage.js";
import ContactTableView from "../components/ContactTableView.js";
import DeleteContactButton from "../components/DeleteContactButton.js";

import { useRole } from "../hooks/useRole.js";

import SnackbarFeedback from "../pages/SnackbarFeedback.js";


// 🧩 Composant principal ContactTable
const ContactTable = ({ groupeCree, groupeId, nomGroupe, createur, snackbar, setSnackbar, emailConnect, onContactsUpdated, sondageVerrouille}) => {
   // 📝 Log des props reçues
  logDev("[ContactTable] props", { groupeCree, groupeId, nomGroupe, createur, emailConnect });

  // 🔐 Vérification du verrouillage
  logDev("[ContactTable] sondageVerrouille reçu", sondageVerrouille);
  
  // 🧠 États locaux
  const { t , lang} = useLanguage();

  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ nom: "", indicatif: "+33", numero: "", mail: "" });
  const isMobile = useMediaQuery("(max-width:600px)");
  const [vueFiches, setVueFiches] = useState(true);

  // 📡 Récupération du rôle au montage
  const role = useRole();
  const isAdmin = role === "admin";

  const [search, setSearch] = useState("");

  const filteredContacts = contacts.filter(c =>
    c.nom?.toLowerCase().includes(search.toLowerCase()) ||
    c.telephone?.toLowerCase().includes(search.toLowerCase()) ||
    c.mail?.toLowerCase().includes(search.toLowerCase())
  );


  logDev("[ContactTable] rôle utilisateur", { role, isAdmin });

  // ✅ Notifier le parent à chaque changement de contacts
  useEffect(() => {
    if (onContactsUpdated) {
      logDev("[ContactTable] onContactsUpdated déclenché", { nbContacts: contacts.length });
      onContactsUpdated(contacts);
    }
  }, [contacts, onContactsUpdated]);

  // Récupération des contacts quand groupeId change
  useEffect(() => {
    if (!groupeId) return;
    logDev("[ContactTable] récupération contacts pour groupe", groupeId);

      axios.get(`${process.env.REACT_APP_API}/contacts?groupe=${groupeId}`)
        .then((res) => {
      
          const tri = res.data.sort((a, b) =>
        a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' })
        );
      logDev("[ContactTable] contacts reçus", { brut: res.data, triés: tri });
      setContacts(tri);
    })
    .catch((err) => logDev("❌ Erreur chargement contacts", { message: err.message, stack: err.stack }));
}, [groupeId]);

  // ⛔ Aucun groupe sélectionné : affiche un message d’information
  if (!groupeId) {
  return (
    <Box sx={{ padding: 4, textAlign: "center", color: "gray" }}>
      ⛔ {t('NoGroupSelected')}<br />
         {t('CreateOrChooseAGroupToManageContacts')}
    </Box>
  );
}

  // ➕ Ajout d’un contact dans le groupe
  const handleAdd = () => {
    if (sondageVerrouille) {
    logDev("⛔ Ajout bloqué — sondage verrouillé");
    setSnackbar({
      open: true,
      message: t("SurveyLockedContactMessage"),
      severity: "warning"
    });
    return;
  }
    if (!isAdmin) return alert("Action réservée à l'administrateur");
    if (!newContact.nom) return alert("Le nom est obligatoire");
    if (!groupeId) return alert("Groupe manquant");

    // 🔧 Construction du numéro complet uniquement si numero est renseigné
    let cleanedTelephone = null;
    if (newContact.numero?.trim()) {
      const fullNumber = buildFullNumber(newContact.indicatif, newContact.numero);
    if (!isValidPhone(fullNumber)) {
    return alert(t("InvalidPhoneFormat"));
    }
  cleanedTelephone = nettoyerPhone(fullNumber);
}

  const contactAvecGroupe = { 
    ...newContact, 
    telephone: cleanedTelephone,
    groupe: groupeId };
      
  logDev("[ContactTable] ajout contact", contactAvecGroupe);

  axios.post(`${process.env.REACT_APP_API}/contacts`, contactAvecGroupe)
    .then((res) => {
      logDev("[ContactTable] contact ajouté", res.data);
      setContacts([...contacts, res.data].sort((a, b) =>
      a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' })
      ));
      
      setNewContact({ nom: "",  indicatif: "+33", numero: "", mail: "" });

      setSnackbar({
        open: true,
        message: `${newContact.nom} ${t("ContactAdded") || "ajouté avec succès !"}`,
        severity: "success",
        duration: 8000 
        });
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          logDev("🚫 Ajout bloqué — quota dépassé", err.response.data);
    
        setSnackbar({
          open: true,
          message: t("QuotaContactsReached"),
          severity: "error",
          duration: 8000
        });
  
        } else {
        console.error("Erreur ajout contact :", err);
    
        setSnackbar({
          open: true,
          message: t("ErreurOccuredAddingContact"),
          severity: "error",
          duration: 8000
        });
      }
    });
  };

  const handleContactsSelected = async (contactsImportes) => {
  if (!isAdmin) return;

  if (sondageVerrouille) {
    setSnackbar({
      open: true,
      message: t("SurveyLockedContactMessage"),
      severity: "warning"
    });
    return;
  }

  if (!groupeId || !contactsImportes?.length) return;

  for (const contact of contactsImportes) {
    const contactAvecGroupe = {
      ...contact,
      groupe: groupeId
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/contacts`,
        contactAvecGroupe
      );

      setContacts(prev =>
        [...prev, res.data].sort((a, b) =>
          a.nom.localeCompare(b.nom, "fr", { sensitivity: "base" })
        )
      );

    } catch (err) {
      if (err.response?.status === 403) {
        setSnackbar({
          open: true,
          message: t("QuotaContactsReached"),
          severity: "error",
          duration: 8000
        });

        // On arrête si le quota est atteint
        break;
      }

      console.error("Erreur ajout contact importé :", err);
    }
  }

  setSnackbar({
    open: true,
    message: t("ContactAdded") || "Contacts ajoutés avec succès !",
    severity: "success",
    duration: 8000
  });
};

  // 🗑️ Suppression d’un contact par ID 
  const handleDelete = (id) => {
     if (!isAdmin) return alert("Action réservée à l'administrateur");
     logDev("[ContactTable] suppression contact", id);

      axios.delete(`${process.env.REACT_APP_API}/contacts/${id}`)
        .then(() => {
          setContacts(contacts.filter(c => c._id !== id));
        })
      .catch((err) => console.error("Erreur suppression :", err));
  };

  // ✏️ Mise à jour d’un contact
  const handleUpdate = (updatedContact) => {
    logDev("[ContactTable] mise à jour contact", updatedContact);
    setContacts(prev =>
      prev.map(c => c._id === updatedContact._id ? updatedContact : c)
    );
  };



  // 🧾 Définition des colonnes du tableau
  const columns = [
    {
      field: "nom",
      headerName: isMobile ? "👤" : t('Name'),
      flex: 1,
      editable: isAdmin && !sondageVerrouille,
      width: isMobile ? 60 : 100,
      pinned: 'left',
      renderCell: (params) => {
        const { nom, telephone, mail } = params.row;
        return (
          <Tooltip
            title={
              isMobile
                ? `${nom}\n${telephone}`
                : (
                  <>
                    <div><strong>{t('Name')}:</strong> {nom}</div>
                    <div><strong>{t('Phone')}:</strong> {telephone}</div>
                    <div><strong>{t('Email')}:</strong> {mail}</div>
                  </>
                )
            }
            arrow
            placement="top"
           >
            <span>{nom}</span>
          </Tooltip>
        );
        }
      },

      { field: "telephone", 
        headerName: isMobile ? "📞" : t('Phone'), 
        flex: 1, 
        minWidth: 114, 
        editable: isAdmin && !sondageVerrouille,
        renderEditCell: (params) => {
          const rawValue = params.value || "";
          const displayValue = normalizePhoneNumber(rawValue, "FR"); // ou "ES", "GB", etc. selon le contexte

        return (
          <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={14}
          value={displayValue}
          onChange={(e) => {
            const normalized = normalizePhoneNumber(e.target.value, "FR");
            params.api.setEditCellValue({
              id: params.id,
              field: "telephone",
              value: normalized
            });
          }}
          onBlur={() => {
            params.api.stopCellEditMode({ id: params.id, field: "telephone" });
          }}
          onKeyDown={(e) => {
  if (e.key === "Enter") {
    params.api.stopCellEditMode({ id: params.id, field: "telephone" });
  }
          }}

          style={{
            width: "100%",
            padding: "8px",
            fontSize: "14px",
            border: "1px solid #ccc",
            borderRadius: "4px"
          }}
          
      />
    );
  }
},
      { field: "mail", 
        headerName: isMobile ? "✉️" : t('Email'), 
        flex: 1, 
        editable: isAdmin && !sondageVerrouille,
      },
      {
        field: "actions",
        headerName: isMobile ? "⚙️" : t('Actions'),
        width: isMobile ? 60 : 100, // ✅ réduit la largeur sur mobile
        sortable: false,
        renderCell: (params) => (
          isAdmin && (  
            <DeleteContactButton
              onDelete={handleDelete}
              id={params.row._id}
              isMobile={isMobile}
              t={t}
              sondageVerrouille={sondageVerrouille}
            />
          )
        )
      }
    ];

  // 🧱 Rendu principal du tableau et du formulaire 
  return (
    <div className={`contact-table-wrapper ${!groupeCree ? "grise" : ""}`}> 

    {contacts.length > 0 && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          marginBottom: isMobile ? "24px" : "48px"
        }}
      >
       {/* Champ de recherche PETIT à gauche */}
        <input
          type="text"
          placeholder={t("SearchContact")}  
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "200px", 
            padding: "6px 10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            fontSize: "0.9rem"
          }}
        />
         {/* Toggle à droite */}
        <ContactViewToggleButton
          vueFiches={vueFiches}
          setVueFiches={setVueFiches}
          isMobile={isMobile}
          t={t}
        />    
      </div>
    )}


   {vueFiches && filteredContacts.length > 0 ? (
    <ContactCardsView
      contacts={filteredContacts}
      isAdmin={isAdmin}
      handleDelete={handleDelete}
      handleUpdate={handleUpdate}
      sondageVerrouille={sondageVerrouille}
    />
) : (
  <>
    {contacts.length === 0 ? (
      <EmptyStateMessage t={t} isMobile={isMobile} />
    ) : (
      <>
        <ContactTableView
          contacts={filteredContacts}
          columns={columns}
          groupeId={groupeId}
          setContacts={setContacts}
          processRowUpdate={createContactRowUpdate(groupeId, setContacts)}
        />
      </>
    )}
  </>
)}

    {/* ➕ Formulaire d’ajout de contact (visible uniquement pour le créateur) */}  
      {/* Formulaire d’ajout */}
      {isAdmin && !sondageVerrouille && (
        <AddContactForm
          t={t}
          newContact={newContact}
          setNewContact={setNewContact}
          handleAdd={handleAdd}
          handleContactsSelected={handleContactsSelected}
        />
      )}
      <BoutonAllerAuSondage label={t("GoToSurvey")} />
    </div>
  );
};

export default ContactTable;
